import {getProjectionDef} from '@apps-common/geocorn-sdk/api/geometry.js';
import importer, {projTransform} from '@apps-common/importer';
import {bbox} from '@turf/bbox';
import {containsExtent} from 'ol/extent.js';
import {EXTENT as WORLD_EXTENT} from 'ol/proj/epsg4326.js';

export const MAX_FEATURES = 5000;

const getBuiltInProjections = code => {
  return new Promise((resolve, reject) => {
    // web mercator
    if (code === '3857') {
      resolve(
        '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs',
      );
      // support WGS84 UTM
    } else if (
      code.length === 5 &&
      (code.substring(0, 3) === '326' || code.substring(0, 3) === '327')
    ) {
      const hemisphere = code.substring(2, 3) === '6' ? 'north' : 'south';
      const zone = code.substring(3);
      resolve(
        `+proj=utm +zone=${zone} +${hemisphere} +datum=WGS84 +units=m +no_defs +type=crs`,
      );
    } else {
      // attempt to fetch the definition from Granero
      getProjectionDef(code)
        .then(def => {
          resolve(def.proj4);
        })
        .catch(() => {
          reject(new Error('Failed to find a valid projection'));
        });
    }
  });
};

export const importFeatures = (files, getProjection = getBuiltInProjections) =>
  importer(files)
    .then(collection => {
      // A bit of a malformed GeoJSON file.
      // The real standard does not allow for this but an earlier
      //  version did. This tries to parse the EPSG code but will
      //  otherwise reject the file.
      if (collection.crs) {
        // this is likely a GeoJSON or a EWKT that came in
        //  with an EPSG code. Importer does not handle that
        //  directly.
        const crs = collection.crs;
        if (crs.type === 'name' && crs.properties && crs.properties.name) {
          const name = crs.properties.name;
          if (name.indexOf('EPSG') >= 0) {
            // The CRS code can come in as EPSG:4326 OR as EPSG::4326,
            //  using just ':' and using pop() ensures the number is
            //  returned.
            const code = crs.properties.name.split(':').pop();

            // do not do any work if the projection is already correct.
            if (code === '4326') {
              return collection;
            }

            return getProjection(code)
              .then(prjText => projTransform(prjText, collection))
              .catch(() => {
                // the projection is bad/unknown, toss an error.
                throw new Error('uploadDialog.unknownCRS');
              });
            // this is the "standard" WGS84 projection,
            //   no reprojection needed.
          } else if (name.indexOf('CRS84') > 0) {
            return collection;
          }
        }
        // the projection is bad/unknown, toss an error.
        throw new Error('uploadDialog.unknownCRS');
      } else {
        // The projection step was only offering WGS84 and Web Mercator,
        //  assume that if the collection of features is within the
        //  WGS84 boundaries, it is WGS84, otherwise, reproject.
        const collectionBBox = bbox(collection);

        // check a "buffered" version of world extent because
        //  sometimes a point will be errantly across the dateline
        if (!containsExtent([-300, -120, 300, 120], collectionBBox)) {
          // Assume this is from web mercator.
          return getProjection('3857').then(prjText =>
            projTransform(prjText, collection),
          );
        }
      }
      // no projection hint, no worry.
      return collection;
    })
    .then(collection => {
      const collectionBBox = bbox(collection);
      if (!containsExtent(WORLD_EXTENT, collectionBBox)) {
        throw new Error('uploadDialog.invalidCoordinates');
      }
      return collection;
    })
    .then(collection => {
      // filter out the features that do not work with Explorer
      const features = collection.features
        .filter(
          feature =>
            feature.geometry &&
            (feature.geometry.type === 'Polygon' ||
              feature.geometry.type === 'MultiPolygon') &&
            // the importer does not reject features, and
            //  occasionally will let some through without a geometry.
            feature.geometry.coordinates.length > 0,
        )
        .slice(0, MAX_FEATURES);

      const warnings = [];
      let maxFeaturesWarning = '';
      const errors = [];

      // this warning message is keyed to the original count of
      //  features not the filtered version.
      if (collection.features.length > MAX_FEATURES) {
        maxFeaturesWarning = 'uploadDialog.maxFeaturesWarning';
      }

      if (features.length === 0) {
        errors.push('uploadDialog.noValidFeaturesError');
      } else if (collection.importer) {
        if (collection.importer.datasetCount > 1) {
          warnings.push('uploadDialog.multipleDatasetsWarning');
        }

        if (
          collection.importer.geometryTypes.filter(
            type => type.indexOf('Polygon') < 0,
          ).length > 0
        ) {
          warnings.push('uploadDialog.nonPolygonWarning');
        }
      }
      return {
        features,
        warnings,
        maxFeaturesWarning,
        errors,
      };
    });
